import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import { Button } from '@narvar/hum';
import { EditorWrapper, FooterBar } from './style';

/*
 * The conditions a schema may name as a footer button's `disabled` value.
 * These strings used to be passed to eval(), which a Content-Security-Policy
 * without 'unsafe-eval' refuses, so the button threw on render (CPMT-5943).
 * Add a condition here rather than reintroducing eval.
 */
const DISABLED_WHEN = new Map([
  ['!localChanges', ({ localChanges }) => !localChanges],
]);

// An unrecognised condition disables the button: failing closed keeps a
// save or cancel from being clickable by mistake.
const resolveDisabled = (disabled, state) => {
  if (typeof disabled !== 'string') return disabled;

  // A Map, not an object, so names like 'toString' can't resolve to
  // something on the prototype.
  const condition = DISABLED_WHEN.get(disabled);
  if (!condition && __DEV__) {
    // Failing closed is silent, so say why the button never enables.
    // eslint-disable-next-line no-console
    console.warn(`CodeBox: unknown disabled condition "${disabled}"`);
  }
  return condition ? condition(state) : true;
};

const FooterButton = ({
  actions,
  action,
  label,
  disabled,
  localChanges,
  ...props
}) => {
  const onClick =
    typeof action === 'string' && Object.keys(actions).includes(action)
      ? actions[action]
      : action;

  const isDisabled = resolveDisabled(disabled, { localChanges });

  return (
    <Button onClick={onClick} disabled={isDisabled} {...props}>
      {label}
    </Button>
  );
};

const FooterButtons = ({ footerButtons, actions, localChanges }) => {
  if (!footerButtons) return null;

  return (
    <FooterBar>
      {footerButtons.map(button => (
        <FooterButton
          {...{ localChanges, actions, ...button }}
          key={button.action}
        />
      ))}
    </FooterBar>
  );
};

FooterButtons.propTypes = {
  footerButtons: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      label: PropTypes.node,
      // A string must be a key of DISABLED_WHEN.
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    }),
  ),
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  localChanges: PropTypes.bool.isRequired,
};

FooterButtons.defaultProps = {
  footerButtons: undefined,
};

export const CodeBox = ({ formControl, data }) => {
  const {
    onChange,
    onSave,
    name,
    language,
    footerButtons = [],
  } = formControl.props;
  const [code, setCode] = useState(data);
  const [savedCode, setSavedCode] = useState(data);
  const [localChanges, setLocalChanges] = useState(false);
  const monacoRef = useRef(null);

  const handleEditorDidMount = editor => {
    monacoRef.current = editor;
    monacoRef.current.trigger('anyString', 'editor.action.formatDocument');
  };

  const handleFormat = () => {
    monacoRef.current.trigger('anyString', 'editor.action.formatDocument');
  };

  const handleChange = value => {
    setCode(value);
    setLocalChanges(true);
    if (onChange) {
      onChange({
        target: { value },
        currentTarget: { name, value, source: 'codeBox' },
      });
    }
  };

  const handleSave = () => {
    const value = monacoRef.current.getValue();
    setSavedCode(value);
    setLocalChanges(false);
    if (onSave) {
      onSave({
        target: { value },
        currentTarget: { name, value, source: 'codeBox' },
      });
    }
  };

  const handleCancel = () => {
    setCode(savedCode);
    setLocalChanges(false);
  };

  const actions = {
    save: handleSave,
    cancel: handleCancel,
  };

  return (
    <div>
      <Button onClick={handleFormat}>Auto Align Code</Button>
      <EditorWrapper>
        <Editor
          {...(language ? { language } : {})}
          defaultValue={data}
          value={code}
          onMount={handleEditorDidMount}
          onChange={handleChange}
          minimapEnabled={false}
          options={{
            minimap: {
              enabled: false,
            },
            tabSize: 2,
            lineNumbers: 'off',
          }}
        />
      </EditorWrapper>
      <FooterButtons {...{ footerButtons, actions, localChanges }} />
    </div>
  );
};
